import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { useGetIntegrationToken } from '../../../api/users'
import { useAbundanceEngineParameters, useParameterReplacementEx } from '../../../hooks/useAbundanceEngineParameters'
import { useSearchClients } from '../../../api/clients'
import LoadingView from '../../pages/LoadingView'
import ErrorView from '../../pages/ErrorView'
import SydButton from '../../commonDesign/Button'
import { ICON_NAMES } from '../../../constants'
import IntegrationAuthDialog from './IntegrationAuthDialog'

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden'
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    display: 'block'
  }
}))

const IntegrationIframe = ({
  integration,
  baseUrl,
  params = {},
  className,
  title,
  sandbox = 'allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox',
  allow = 'fullscreen',
  width = '100%',
  height = '100%',
  allowAuth = true,
  authLabels = {
    connectButton: 'Get started',
    buttonIcon: ICON_NAMES.link,
    title: 'Authentication required',
    subtitle: 'Please enter your credentials',
    modalTitle: 'Connect',
    modalSubtitle: 'Please enter your credentials to connect'
  },
  errorLabels = {
    title: 'Error loading data',
    subtitle: 'An error occurred while loading this data. Please try again later.'
  }
}) => {
  const classes = useStyles()
  const [authDialogOpen, setAuthDialogOpen] = useState(false)
  const abundanceParams = useAbundanceEngineParameters()

  const {
    data: tokenData,
    isLoading: isLoadingToken,
    error,
    refetch
  } = useGetIntegrationToken(integration, 201, abundanceParams.clientId, {
    enabled: !!integration && !!abundanceParams.clientId
  })

  const { data: clientContextData, isLoading: isLoadingClientContextData } = useSearchClients(
    {
      filters: { clientId: abundanceParams?.clientId },
      includes: { integrations: true }
    },
    { enabled: !!abundanceParams?.clientId, mapper: data => data?.data?.[0] || null }
  )

  const replacementData = useMemo(() => ({
    ...tokenData,
    ...(clientContextData ? {
      orionClientId: clientContextData.orionClientId,
      clientId: clientContextData.clientId
    } : {})
  }), [tokenData, clientContextData])

  const [resolvedParams, missingVariables] = useParameterReplacementEx(params, replacementData)
  const buildUrl = ({ path = '', params = {} }) => {
    const filtered = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null)
    )
    const qs = new URLSearchParams(filtered).toString()
    return qs ? `${path}?${qs}` : path
  }

  const finalParams = useMemo(() => {
    return Object.entries(resolvedParams).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value?.path ? buildUrl(value) : value
      }
      return acc
    }, {})
  }, [resolvedParams])

  const requiresAuth = useMemo(() => !!error?.response?.requiresAuth, [error])

  const handleConnectClick = useCallback(() => {
    setAuthDialogOpen(true)
  }, [])

  const isLoading = useMemo(() => isLoadingToken || isLoadingClientContextData, [isLoadingToken, isLoadingClientContextData])

  const iframeUrl = useMemo(() => {
    if (!baseUrl || !tokenData) return null

    if (missingVariables && missingVariables.length > 0) {
      console.warn('IframeIntegration: Missing variables', missingVariables)
      return null
    }

    try {
      const url = new URL(baseUrl)
      if (finalParams) {
        Object.entries(finalParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            url.searchParams.set(key, value)
          }
        })
      }
      return url.toString()
    } catch (err) {
      console.error('Error constructing iframe URL:', err)
      return null
    }
  }, [baseUrl, tokenData, finalParams, missingVariables])

  if (isLoading) return <LoadingView width='100%' height='100%' />
  if (error && requiresAuth && allowAuth) {
    return (
      <div className='flex flex-col items-center justify-center' style={{ width, height }}>
        <div className='flex flex-col items-center justify-center gap-2'>
          <p className='font-size-2xl text-center mb-0'>
            {authLabels.title || 'Authentication Required'}
          </p>
          {authLabels.subtitle?.length && (
            <p className='font-size-md text-center mt-0 mb-4'>
              {authLabels.subtitle}
            </p>
          )}
          <SydButton
            size='lg'
            variant='primary'
            onClick={handleConnectClick}
            icon={authLabels.buttonIcon}
          >
            {authLabels.connectButton || 'Get Started'}
          </SydButton>
        </div>
        <IntegrationAuthDialog
          open={authDialogOpen}
          onClose={() => setAuthDialogOpen(false)}
          onSubmit={refetch}
          inputs={error.response?.authInputs || []}
          integration={integration}
          title={authLabels.modalTitle}
          subtitle={authLabels.modalSubtitle}
        />
      </div>
    )
  }

  if (error) {
    return (
      <ErrorView
        error={{
          message: errorLabels.title,
          details: errorLabels.subtitle
        }}
      />
    )
  }

  if (!iframeUrl) {
    return (
      <ErrorView
        error={{
          message: 'Unable to construct integration URL. Please check your configuration.'
        }}
      />
    )
  }

  return (
    <div className={`${classes.container} ${className || ''}`} style={{ width, height }}>
      <iframe
        src={iframeUrl}
        className={classes.iframe}
        title={title || `${integration} integration`}
        sandbox={sandbox}
        allow={allow}
        referrerPolicy='strict-origin-when-cross-origin'
      />
    </div>
  )
}

IntegrationIframe.propTypes = {
  integration: PropTypes.string.isRequired,
  baseUrl: PropTypes.string.isRequired,
  params: PropTypes.object,
  className: PropTypes.string,
  title: PropTypes.string,
  sandbox: PropTypes.string,
  allow: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  allowAuth: PropTypes.bool,
  authLabels: PropTypes.shape({
    connectButton: PropTypes.string,
    buttonIcon: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    modalTitle: PropTypes.string,
    modalSubtitle: PropTypes.string
  }),
  errorLabels: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string
  })
}

export default IntegrationIframe
